import moment from 'moment'
import * as uid from 'uid'
import { PrecotationDto, CCAMDto, NGAPDto } from '@services/dtos'

/**
 * Returns a valid date string in YYYY-MM-DD format
 * Falls back to today if the input is invalid
 */
export const getValidDateOrToday = (date: any): string => {
  if (!date) return moment().format('YYYY-MM-DD')
  const m = moment(date)
  return m.isValid() ? m.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
}

/**
 * Validates and formats a date for execution
 * Returns undefined if the date is invalid (for optional fields)
 */
export const getValidDateOrUndefined = (date: any): string | undefined => {
  if (!date) return undefined
  const m = moment(date)
  return m.isValid() ? m.format('YYYY-MM-DD') : undefined
}

/**
 * Creates a new precotation row with default values
 */
export const createPrecotationRow = (params: {
  medicalOrderId: number
  cotationType: 'CCAM' | 'NGAP'
  code?: string
  price?: number
  modificateur?: string
  registeredAt?: string | Date
}): PrecotationDto => {
  const {
    medicalOrderId,
    cotationType,
    code = '',
    price = 0,
    modificateur = '',
    registeredAt,
  } = params

  return {
    cotation: code,
    cotation_type: cotationType,
    prix_unitaire: price,
    id_medical_order: medicalOrderId,
    identifier: uid.uid(10).toString(),
    modificateur,
    date_execution: getValidDateOrToday(registeredAt),
  }
}

export const getUniqueCotationCodes = (codes?: string[] | null): string[] => {
  if (!codes?.length) return []

  return Array.from(
    new Set(codes.map((code) => code?.trim()).filter(Boolean) as string[]),
  )
}

/**
 * Creates precotation rows from CCAM codes with prices
 */
export const createCCAMPrecotations = (params: {
  codes: string[]
  ccamData: CCAMDto[]
  medicalOrderId: number
  registeredAt?: string | Date
}): PrecotationDto[] => {
  const { codes, ccamData, medicalOrderId, registeredAt } = params

  return codes.map((code) => {
    const ccam = ccamData.find((c) => c.code === code)
    return createPrecotationRow({
      medicalOrderId,
      cotationType: 'CCAM',
      code,
      price: ccam?.prixNonOptam ?? 0,
      modificateur: ccam?.modificateurs ?? '',
      registeredAt,
    })
  })
}

/**
 * Creates precotation rows from NGAP codes with prices
 */
export const createNGAPPrecotations = (params: {
  codes: string[]
  ngapData: NGAPDto[]
  medicalOrderId: number
  registeredAt?: string | Date
}): PrecotationDto[] => {
  const { codes, ngapData, medicalOrderId, registeredAt } = params

  return codes.map((code) => {
    const ngap = ngapData.find((n) => n.code === code)
    return createPrecotationRow({
      medicalOrderId,
      cotationType: 'NGAP',
      code,
      price: ngap?.prix ?? 0,
      registeredAt,
    })
  })
}

/**
 * Prepares precotations for saving by:
 * - Building modificateur from m1-m4 fields
 * - Validating dates
 * - Filtering out empty cotations
 */
export const preparePrecotationsForSave = (
  precotations: PrecotationDto[],
  fallbackDate?: string | Date,
): PrecotationDto[] => {
  return precotations.reduce((acc, pr) => {
    // Skip empty cotations
    if (!pr.cotation) return acc

    const prepared = { ...pr, modificateur: '' }

    // Build modificateur from m1-m4 fields
    const modifierFields = ['m1', 'm2', 'm3', 'm4'] as const
    for (const field of modifierFields) {
      if (prepared[field]) {
        prepared.modificateur += prepared[field]
      }
    }

    // Validate date_execution
    if (
      !prepared.date_execution ||
      !moment(prepared.date_execution).isValid()
    ) {
      prepared.date_execution = getValidDateOrToday(fallbackDate)
    }

    // Validate date_entente_prealable (optional field)
    if (
      prepared.date_entente_prealable &&
      !moment(prepared.date_entente_prealable).isValid()
    ) {
      prepared.date_entente_prealable = undefined
    }

    acc.push(prepared)
    return acc
  }, [] as PrecotationDto[])
}

/**
 * Groups precotations by medical order ID
 */
export const groupPrecotationsByMedicalOrder = (
  precotations: PrecotationDto[],
): Map<number, PrecotationDto[]> => {
  return precotations.reduce((acc, pr) => {
    const medicalOrderId = pr.id_medical_order
    if (!medicalOrderId) return acc

    if (acc.has(medicalOrderId)) {
      acc.get(medicalOrderId)?.push(pr)
    } else {
      acc.set(medicalOrderId, [pr])
    }
    return acc
  }, new Map<number, PrecotationDto[]>())
}

/**
 * Extracts unique cotation codes from precotations
 */
export const extractUniqueCodes = (
  precotations: PrecotationDto[],
  type: 'CCAM' | 'NGAP',
): string[] => {
  const codes = precotations
    .filter((pr) => pr.cotation && pr.cotation_type === type)
    .map((pr) => pr.cotation as string)
  return Array.from(new Set(codes))
}

/**
 * Merges cotation codes from precotations and exam defaults
 */
export const mergeCotationCodes = (
  precotations: PrecotationDto[],
  examCodes: string[],
  type: 'CCAM' | 'NGAP',
): string[] => {
  const fromPrecots = extractUniqueCodes(precotations, type)
  return Array.from(new Set([...fromPrecots, ...examCodes]))
}

/**
 * Parses modifier string into m1-m4 fields
 */
export const parseModifiers = (precotation: PrecotationDto): PrecotationDto => {
  if (!precotation.modificateur) return precotation

  const modifiers = precotation.modificateur.split('')
  const result = { ...precotation }

  modifiers.forEach((m, i) => {
    if (i < 4) {
      result[`m${i + 1}` as 'm1' | 'm2' | 'm3' | 'm4'] = m
    }
  })

  return result
}

/**
 * Enriches precotations with CCAM/NGAP data (prices, modifiers)
 */
export const enrichPrecotationsWithPrices = (
  precotations: PrecotationDto[],
  ccamData: CCAMDto[],
  ngapData: NGAPDto[],
): PrecotationDto[] => {
  return precotations.map((pr) => {
    if (pr.cotation_type === 'CCAM') {
      const ccam = ccamData.find((c) => c.code === pr.cotation)
      if (ccam) {
        const enriched = parseModifiers({
          ...pr,
          prix_unitaire: ccam.prixNonOptam,
          modificateur: ccam.modificateurs,
        })
        return enriched
      }
    } else if (pr.cotation_type === 'NGAP') {
      const ngap = ngapData.find((n) => n.code === pr.cotation)
      if (ngap) {
        return { ...pr, prix_unitaire: ngap.prix }
      }
    }
    return pr
  })
}

/**
 * Checks if billing mode requires AMO situation update
 */
export const shouldUpdateSituationAmo = (mode: number): boolean => {
  return mode === 0 || mode === 2
}

/**
 * Checks if a base64 string is valid
 */
export const isBase64 = (str: string): boolean => {
  const base64Regex =
    /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/
  return base64Regex.test(str)
}
